import React from "react";

/**
 * Polar bear peek/wave above the shortlist cart (desktop FAB + mobile bottom bar).
 * Renders behind the cart (z-49) so lower body is hidden by the cart (z-50).
 */
export default function CartMascotNudge({ open }) {
  const [mounted, setMounted] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  const exitTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setMounted(true);
      setExiting(false);
      return undefined;
    }

    if (!mounted) return undefined;
    setExiting(true);
    if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      setMounted(false);
      setExiting(false);
    }, 560);
    return undefined;
  }, [open, mounted]);

  React.useEffect(
    () => () => {
      if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
    },
    []
  );

  if (!mounted) return null;

  return (
    <>
      <style>{`
        @keyframes cartMascotPop {
          0%   { transform: translateY(105%); }
          100% { transform: translateY(0); }
        }
        @keyframes cartMascotPopBack {
          0%   { transform: translateY(0); }
          100% { transform: translateY(105%); }
        }
        @keyframes cartMascotWave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          50% { transform: rotate(4deg); }
          75% { transform: rotate(-2deg); }
        }
        @keyframes cartMascotBubble {
          from { opacity: 0; transform: translate(6px, 4px) scale(0.92); }
          to   { opacity: 1; transform: translate(0, 0) scale(1); }
        }
        @keyframes cartMascotBubbleOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        .cart-mascot-pop {
          animation: cartMascotPop 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform;
        }
        .cart-mascot-pop.is-exiting {
          animation: cartMascotPopBack 0.55s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .cart-mascot-wave {
          transform-origin: 72% 18%;
          animation: cartMascotWave 1.35s ease-in-out 0.75s 2;
          will-change: transform;
        }
        .cart-mascot-wave.is-exiting {
          animation: none;
        }
        .cart-mascot-bubble {
          animation: cartMascotBubble 0.35s ease-out 0.55s both;
        }
        .cart-mascot-bubble.is-exiting {
          animation: cartMascotBubbleOut 0.25s ease-in forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .cart-mascot-pop { animation: none; transform: none; }
          .cart-mascot-pop.is-exiting { animation: none; opacity: 0; }
          .cart-mascot-wave { animation: none; }
          .cart-mascot-bubble { animation: none; opacity: 1; }
          .cart-mascot-bubble.is-exiting { animation: none; opacity: 0; }
        }
      `}</style>

      {/* Desktop FAB — bottom-24 right-6, same footprint as cart button */}
      <div className="hidden md:block fixed bottom-24 right-6 z-[49] h-[3.75rem] w-[17.5rem] pointer-events-none overflow-visible">
        <div className="absolute bottom-0 left-[-5.3125rem] h-[calc(9rem+3.75rem)] w-[19rem] overflow-hidden">
          <div className={`cart-mascot-pop absolute left-0 top-0 ${exiting ? "is-exiting" : ""}`}>
            <img
              src="/Waving-Photoroom.png"
              alt="Polar bear waving"
              className={`cart-mascot-wave block h-[16.25rem] w-[16.25rem] object-contain object-top select-none drop-shadow-md ${exiting ? "is-exiting" : ""}`}
              draggable={false}
            />
          </div>
        </div>
      </div>
      <div className="hidden md:block fixed bottom-24 right-6 z-[51] h-[3.75rem] w-[17.5rem] pointer-events-none overflow-visible">
        <div
          className={`cart-mascot-bubble absolute bottom-full left-[calc(-5.3125rem+10.5rem)] mb-[5.75rem] rounded-2xl bg-white px-2.5 py-1.5 text-[11px] font-semibold leading-none text-slate-800 shadow-lg border border-sky-100 whitespace-nowrap ${exiting ? "is-exiting" : ""}`}
        >
          Apply from here 👋
        </div>
      </div>

      {/* Mobile bottom shortlist bar — peek from behind the left side of the bar */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-[49] pointer-events-none overflow-visible"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="relative h-14 w-full">
          <div className="absolute bottom-0 left-0 h-[calc(6rem+3.5rem)] w-[11rem] overflow-hidden">
            <div className={`cart-mascot-pop absolute left-0 top-0 ${exiting ? "is-exiting" : ""}`}>
              <img
                src="/Waving-Photoroom.png"
                alt="Polar bear waving"
                className={`cart-mascot-wave block h-[11rem] w-[11rem] object-contain object-top select-none drop-shadow-md ${exiting ? "is-exiting" : ""}`}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-[51] pointer-events-none overflow-visible"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="relative h-14 w-full">
          <div className="absolute bottom-full left-[6.5rem] mb-[5rem]">
            <div
              className={`cart-mascot-bubble rounded-2xl bg-white px-2 py-1 text-[10px] font-semibold leading-none text-slate-800 shadow-lg border border-sky-100 whitespace-nowrap ${exiting ? "is-exiting" : ""}`}
            >
              Apply from here 👋
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
